/**
 * The week's co-op challenge, in one card, shared by Missions and the Scoreboard.
 *
 * It lives in both because it is two different things depending on where you are standing: on
 * Missions it is tonight's question, and on the Scoreboard it is a shared score. Rendering it
 * twice from one component is what keeps the standing identical in both — a challenge that said
 * "4 of 7" on one tab and "3 of 7" on the other would be a bug about trust, not about counting.
 *
 * Logging is per night, one row per local day, and the first log stands. A second tap says so
 * rather than quietly doing nothing, because "did that register?" is the question a silent
 * no-op provokes.
 *
 * The standing is drawn as ONE OBJECT both phones keep alive — Flora's shared tree. A bar plus two
 * counters is a readout of a number each partner can privately decide is the other's problem; a
 * plant that is visibly wilting is a thing in the room. The bar and the counts survive as the
 * caption under it, because "3 of 5, one miss left" is the fact and the plant is the feeling, and
 * dropping the fact would make the card unable to answer the question it exists for.
 *
 * APP-LOCAL COMPOSITION, not a design-system component, and deliberately absent from
 * `_design.md`'s components: catalogue. It is one product feature — the week's co-op challenge,
 * its logging rule and Flora's tree — assembled out of Card, Button, Text, LockPair and WhyThis.
 * A second app has no use for it, and the pieces it is made of are the ones the system names.
 */
import { LockPair } from '@/components/lock-pair';
import { WhyThis } from '@/components/why-this';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { otherPartner, type PartnerId } from '@/lib/partner';
import { cn } from '@/lib/utils';
import { useLayout } from '@/useLayout';
import { useStore, type Invite } from '@/store';
import * as React from 'react';
import { View } from 'react-native';

export interface Coop {
  id: string;
  title: string;
  detail: string | null;
  target: number;
  budget: number;
  progress: number;
  misses: number;
  status: 'proposed' | 'active' | 'succeeded' | 'failed';
  proposedBy: 'A' | 'B' | 'app';
  /**
   * One row per local day, ordered by day ascending. `loggedBy` is who got there first — the night
   * counts once, so it is exactly one of the two and never the game.
   */
  log: { day: string; outcome: 'kept' | 'missed'; loggedBy: PartnerId }[];
  today: 'kept' | 'missed' | null;
}

/**
 * Which partner logged tonight, or null when that cannot be established.
 *
 * The SERVER decides what "today" is — one fixed scoring timezone — so the client must not
 * recompute the day key: a phone in another timezone would name the wrong row and caption the
 * wrong person. What the payload does give is `today`, which is that row's outcome, plus a log
 * ordered by day. So the candidate is the last row, and it is only believed when its outcome
 * agrees with `today`. Disagreement means the payload is describing a night this client cannot
 * identify, and naming a person for it would be inventing an answer — so neither marker fills.
 */
function loggedTonightBy(coop: Coop): PartnerId | null {
  if (!coop.today) return null;
  const last = coop.log[coop.log.length - 1];
  return last && last.outcome === coop.today ? (last.loggedBy ?? null) : null;
}

export function CoopCard({
  coop,
  invites,
  onChange,
}: {
  coop: Coop;
  invites: Invite[];
  onChange: () => Promise<void>;
}) {
  const { me, acceptInvite, dismissInvite, fail } = useStore();
  const { wide } = useLayout();
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');
  const them = me?.partnerName ?? 'they';

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await onChange();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const log = async (outcome: 'kept' | 'missed') => {
    await run(async () => {
      const result = await api.post<{ alreadyLogged: boolean; outcome: string }>(
        `/api/score/coop/${coop.id}/log`,
        { outcome },
      );
      setNote(
        result.alreadyLogged
          ? `Tonight is already down as “${result.outcome}”. A night counts once, whoever logs it.`
          : outcome === 'kept'
            ? 'Logged. Nice one.'
            : 'Logged. That is what the budget is for.',
      );
    });
  };

  const terms = `${coop.target} nights · ${coop.budget} misses allowed · you both win or lose it together`;

  /** Who logged tonight, as a pair of markers. Only one can ever fill — the first log stands. */
  const tonightBy = loggedTonightBy(coop);
  const tonight = me ? (
    <LockPair
      me={me.partner}
      mine={tonightBy === me.partner}
      theirs={tonightBy === otherPartner(me.partner)}
      mineLabel="you"
      theirsLabel={me.partnerName}
      size="sm"
    />
  ) : null;

  /** The plant stands for every started challenge, including the two it can end in. */
  const started = coop.status === 'active' || coop.status === 'succeeded' || coop.status === 'failed';

  return (
    /* A MOMENT: the garland straddles this card's top edge. The co-op is one of exactly five in
       the app that make the claim, and it is the clearest of them — a thing two people agreed to
       and are keeping alive together is the definition of something having happened. */
    <Card moment>
      <CardContent>
        <CardHead>
          {coop.status === 'proposed' && coop.proposedBy === 'app'
            ? `The game proposes: ${coop.title}`
            : coop.title}
        </CardHead>
        {coop.detail ? <CardDescription>{coop.detail}</CardDescription> : null}
        <Text variant="small" className="text-muted-foreground">
          {terms}
        </Text>

        {coop.status === 'proposed' ? (
          invites.length ? (
            <View className="flex-row flex-wrap gap-2">
              <Button
                disabled={busy}
                onPress={() =>
                  run(async () => {
                    await acceptInvite(invites[0]!);
                    await api.post(`/api/score/coop/${coop.id}/accept`);
                  })
                }>
                <Text>We’re in</Text>
              </Button>
              <Button variant="ghost" flush={false} disabled={busy} onPress={() => run(() => dismissInvite(invites[0]!))}>
                <Text>Not this week</Text>
              </Button>
            </View>
          ) : (
            <Text variant="small" className="text-muted-foreground">
              {/* A challenge the game proposed is nobody's ask, so nobody can be the holdout.
                  This branch appears as soon as the VIEWER dismisses their own invite, so
                  partner-named copy here is a pressure receipt about a choice the partner may
                  never have been offered yet, let alone declined. */}
              {coop.proposedBy === 'app'
                ? 'Not active yet — it starts when you have both opted in.'
                : `Waiting for ${them} to opt in.`}
            </Text>
          )
        ) : null}

        {started ? (
          /**
           * THE PLANT AND ITS FACT, SIDE BY SIDE WHERE THERE IS ROOM.
           *
           * A four-night challenge draws a seedling about forty pixels tall, and on a desktop that
           * was forty pixels centred in an 877px card with two hundred of dead band above and
           * below it — a shared living thing rendered as a broken image. Two things fix it and
           * both are here: the plant gets a STAGE of its own (a fixed 160px box it is scaled to
           * fill, so a one-night challenge and a twelve-night one are the same size of object),
           * and at bp-wide the bar and the count move up BESIDE it instead of leaving air there.
           *
           * Below bp-wide they stack, which is the same two pieces in the same order — this is a
           * width decision and only a width decision.
           */
          <View className={cn('gap-3', wide && 'flex-row items-center')}>
            <Flora coop={coop} />

            {/* The caption: the fact under the feeling. Unchanged from before the plant existed,
                except that it is a 2px-shouldered bar rather than a lozenge — the restyle's own
                radius for a meter — and the fill is the reward colour: Bouquet lights it as a
                gradient through `fit-meter-fill`, Tuxedo keeps it flat as its one red object.
                    Web only: `bg-meter-fill` is the flat fill underneath it, which is what Tuxedo
                    wants and what native gets in both looks. */}
            <View className="min-w-0 flex-1 gap-1.5">
              <View className="bg-track h-2.5 overflow-hidden rounded-[2px]">
                <View
                  className="bg-meter-fill fit-meter-fill h-full rounded-[2px]"
                  style={{ width: `${Math.min(100, (coop.progress / coop.target) * 100)}%` }}
                />
              </View>
              <Text variant="small" className="text-muted-foreground">
                {coop.progress} of {coop.target} kept · {coop.misses} of {coop.budget} misses used
              </Text>
            </View>
          </View>
        ) : null}

        {coop.status === 'active' ? (
          <>
            {/**
             * TONIGHT, AS A MATCHED PAIR.
             *
             * "Tonight: kept" was a filled button and "Tonight: missed" was bare red text, which
             * is one control standing beside one warning rather than two answers to the same
             * question — and at 320 the bare word sat three pixels off the card's edge, outside
             * the gutter every other row in the app keeps. They are two outline buttons of equal
             * width now, and the one you pick FILLS: the fill says which answer you gave, not
             * which answer the app would prefer.
             *
             * Both stay pressable after a log, on purpose and as this file has always argued: a
             * second tap answers "did that register?" with the sentence about a night counting
             * once, where a control that has quietly vanished answers nothing.
             */}
            <View className="flex-row flex-wrap items-center gap-3">
              {/* The pair takes a row of its own (`basis-full`) rather than sharing one with the
                  markers, which was squeezing both labels onto two lines at 390. Inside it they
                  grow off a 140px basis and stop at 240: three across on a desktop card would be
                  two 400px slabs, and at 320 the two of them cannot both fit on one line, so they
                  stack — still a matched pair, still equal width. */}
              <View className="flex-row flex-wrap gap-2 basis-full">
                {(['kept', 'missed'] as const).map((outcome) => (
                  <Button
                    key={outcome}
                    variant={coop.today === outcome ? 'default' : 'outline'}
                    className="grow basis-[140px] max-w-[240px]"
                    accessibilityState={{ selected: coop.today === outcome, disabled: busy }}
                    disabled={busy}
                    onPress={() => log(outcome)}>
                    <Text>Tonight: {outcome}</Text>
                  </Button>
                ))}
              </View>
              {tonight}
            </View>
            {note ? (
              <Text variant="small" className="text-muted-foreground">
                {note}
              </Text>
            ) : null}
          </>
        ) : null}

        {coop.status === 'succeeded' ? (
          <Text className="text-gold-text font-semibold">Done together. +4 each.</Text>
        ) : null}
        {coop.status === 'failed' ? (
          <Text>The budget ran out. You lost this one together, which is the point.</Text>
        ) : null}

        {/* The disclosure sits on the thing itself rather than behind the panel's ⓘ — the co-op is
            a shared budget, and "why would we agree to that" is asked here, not on a help screen.

            IT ANSWERS ITS OWN QUESTION NOW. `id="score"` used to supply the body as well as the
            routing, so "Why a shared budget helps" expanded into the Scoreboard's research about
            play and Fox-in-the-Forest scoring bands — a label and a body about two different
            subjects, which is worse than no footnote. The id stays (Missions only borrows this
            card; the co-op belongs to Score) and the words are the co-op's own. */}
        <WhyThis
          id="score"
          line="Why we both win or lose it"
          claim={
            'One target, counted once for the two of you: a night either of you keeps is a night ' +
            'kept, and a night neither of you does spends from the same small allowance. ' +
            'Neither of you can win it alone, so neither of you can be the one who lost it.'
          }
        />
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Flora — the shared plant.
 *
 * Drawn from Views and nothing else: no react-native-svg anywhere in this app, and a plant is a
 * stem, some lens-shaped leaves and a pot, which rounded rectangles and `rotate` cover exactly.
 *
 * Colour is four tokens and no more, and the plant is the only object in the app that gets its own
 * — a shared living thing is not a button, so it does not take `--primary`. `--plant` and
 * `--plant-2` are the living tissue (the two alternate up the stem, which is what stops a column
 * of identical leaves reading as a fern), `--line-strong` is everything dead or fallen,
 * `--plant-pot` is the pot, and `--gold` is a bud — an unopened night is a reward not yet
 * collected, and gold is what this round pays rewards in. Everything in between is opacity and
 * angle, so the whole thing survives a look swap and invents no palette of its own.
 *
 * ROUND CORNERS SURVIVE HERE. The restyle squared every pressable thing in the app, and a bud is
 * not one: the boards draw these as `<circle>` and `<path>`, and a lozenge stem with 4px-shouldered
 * buds would be a plant drawn by a UI kit.
 * ------------------------------------------------------------------------------------------- */

/**
 * Twelve leaf slots is where a stem stops reading as a plant and starts reading as a comb. Every
 * shipped template targets 2–5 nights, so this is a guard rather than a live constraint — beyond
 * it the slots stand for a PROPORTION of the target rather than one night each, which is why the
 * grown count is scaled rather than clamped (clamping would draw a finished plant as unfinished).
 */
const MAX_SLOTS = 12;

/**
 * THE BOARD'S PLANT, MEASURED. Every number below is read off `concepts/board.html`'s
 * `<svg class="plant" viewBox="0 0 120 100">`, converted from its top-down `y` to the bottom-up
 * coordinates a View lays out in (`bottom = 100 − y`). The prototype drew a different plant —
 * leaves low on both flanks, a leaf fallen to the LEFT — and a restyle that keeps its own
 * arrangement while wearing the board's colours is a restyle that did not happen.
 *
 * The board's composition, in its own numbers:
 *   stem      x 60, 3 wide, y 26→88          — dead centre of a 120 box, its foot in the pot
 *   leaf-1    (60,62) → (34,48)              — LEFT, low, sweeping out and up ~28°
 *   leaf-2    (60,46) → (86,32)              — RIGHT, above it, the same sweep mirrored
 *   buds      x 60, y 30 (r3), 40, 50 (r2.5) — ON the stem, above and between the leaves
 *   fallen    x 86→108, y 72→84              — RIGHT of the pot, on the soil
 *   pot       40→80 at y 86, 44→76 at y 98   — a trapezoid: 40 across the rim, 32 at the foot
 *
 * What generalises and what does not: the board draws five slots, and this has to draw between one
 * and twelve. So the SHAPES and the PROPORTIONS are the board's exactly, and the two things that
 * have to scale — how many slots there are and how far apart they sit — stay a step up the stem.
 * Leaves alternate side going up, starting LEFT like leaf-1; buds sit on the stem, which is why
 * they end up above the leaves without anything having to arrange them (leaves grow from the foot
 * up, so the ungrown slots are the top of the stem — the board's own reading of the same picture).
 */
const LEAF_STEP = 13;
const LEAF_W = 26;
const LEAF_H = 11;
/** Degrees a leaf lifts from horizontal at full health: atan(14/26) on both of the board's leaves. */
const LEAF_TILT = 28;
/** The lowest slot's clearance above the pot's rim. */
const SLOT_RISE = 10;
/** Bare stem above the topmost slot — the board's tip, and where a won bloom opens. */
const STEM_BARE = 12;
const BUD_D = 5;
/** The board's crown bud is r3 against the r2.5 of the two under it. */
const BUD_D_TIP = 6;
const POT_H = 12;
const POT_TOP_W = 40;
const POT_BOT_W = 32;
const PLANT_W = 120;
/**
 * THE STAGE the plant is drawn on: one fixed box, whatever the challenge is.
 *
 * The plant's own geometry is a function of the target — a one-night challenge is a seedling and a
 * twelve-night one is a stalk, which is the growth being in the shape rather than only in the leaf
 * count — and the consequence on a desktop was a forty-pixel object centred in an 877px card with
 * two hundred pixels of dead band above and below it. It read as a broken image, which is a bad
 * outcome for the one thing on the card that is supposed to be alive.
 *
 * So the plant keeps its geometry and gets SCALED to fill a fixed stage: the same size of object
 * every week, and the difference between a short challenge and a long one stays where it belongs,
 * in how tall and busy the plant is relative to itself. `STAGE_PAD` is the air the leaves need at
 * the widest slot, and the scale is capped so a seedling is enlarged rather than blown up.
 */
const STAGE_H = 160;
const STAGE_W = 200;
const STAGE_PAD = 16;
const SCALE_MAX = 1.7;
const SCALE_MIN = 0.6;
/** More fallen leaves than this and they stack on top of each other; the caption carries the count. */
const MAX_FALLEN = 6;
const FALLEN_W = 22;
const FALLEN_H = 10;

function Flora({ coop }: { coop: Coop }) {
  const dead = coop.status === 'failed';
  const won = coop.status === 'succeeded';

  const slots = Math.max(1, Math.min(coop.target, MAX_SLOTS));
  const grown = won
    ? slots
    : Math.min(slots, Math.round((Math.min(coop.progress, coop.target) / Math.max(1, coop.target)) * slots));

  /**
   * How far through the budget the misses are, 0…1 — the continuous half of the wilt.
   *
   * Both ends are pinned rather than computed. A dead plant is 1 because a challenge can fail on
   * the last night with the ratio still reading as survivable; a WON one is 0 because the claim
   * asks for a full-grown state and a plant that made it while spending its whole budget is still
   * a plant that made it. The misses are not erased by that — the fallen leaves on the soil are
   * the record, and they stay.
   */
  const wilt = dead ? 1 : won ? 0 : Math.min(1, coop.misses / Math.max(1, coop.budget));

  /**
   * The stem reaches from the pot's rim to a bare tip above the topmost slot, so a one-night
   * challenge is a seedling and a twelve-night one is a stalk — the growth is in the geometry as
   * well as in the leaf count. `slotBottom` is the one place a slot's height is decided; the leaf,
   * the bud and nothing else read it, which is what keeps a bud sitting exactly where the leaf it
   * is waiting to become would be.
   */
  const stemH = (slots - 1) * LEAF_STEP + SLOT_RISE + STEM_BARE;
  const height = POT_H + stemH;
  const centre = PLANT_W / 2;
  const slotBottom = (i: number) => POT_H + SLOT_RISE + i * LEAF_STEP;

  /** Spoken, because a plant drawn out of Views is a picture to everything that cannot see it. */
  const label = dead
    ? 'The shared plant has died — the miss budget is spent.'
    : won
      ? 'The shared plant is fully grown.'
      : `The shared plant has ${coop.progress} of ${coop.target} leaves` +
        (coop.misses ? `, wilting from ${coop.misses} of ${coop.budget} misses.` : ' and is healthy.');

  /** As big as the stage will take it, in one number both axes have to agree on. */
  const scale = Math.max(
    SCALE_MIN,
    Math.min(SCALE_MAX, (STAGE_H - STAGE_PAD) / height, (STAGE_W - STAGE_PAD) / PLANT_W),
  );

  return (
    <View
      className="items-center justify-center self-center"
      style={{ height: STAGE_H, width: STAGE_W }}
      accessibilityRole="image"
      accessibilityLabel={label}>
      <View
        style={{ width: PLANT_W, height, transform: [{ scale }] }}
        className={dead ? 'opacity-70' : undefined}>
        {/* The stem: the board's 3px line up the middle, its foot in the pot. It leans as the plant
            wilts, which is the one place the whole object moves. */}
        <View
          className={`absolute rounded-full ${dead ? 'bg-line-strong' : 'bg-plant'}`}
          style={{
            left: centre - 1.5,
            bottom: POT_H - 3,
            width: 3,
            height: stemH + 3,
            opacity: dead ? 1 : 1 - wilt * 0.35,
            transform: [{ rotate: `${wilt * 5}deg` }],
          }}
        />

        {/* One slot per night. Grown slots carry a leaf; the rest are buds that have not opened. */}
        {Array.from({ length: slots }, (_, i) => {
          /* Slot 0 is the board's leaf-1, which is LEFT and low; leaf-2 is right and above it. */
          const right = i % 2 === 1;
          const bottom = slotBottom(i);
          if (i >= grown) {
            /* The board's buds sit ON the stem rather than off to one flank — three beads up the
               centre line, the crown one a size larger. Nothing has to arrange them above the
               leaves: leaves fill from the foot up, so the ungrown slots ARE the top of the stem. */
            const d = i === slots - 1 ? BUD_D_TIP : BUD_D;
            return (
              <View
                key={`bud-${i}`}
                /* Gold, at full strength: a bud is a night still to be won, and the boards paint
                   every unopened one in the reward colour rather than greying it out. A grey bud
                   would say the night had been lost, which is what a fallen leaf says. */
                className={`absolute rounded-full ${dead ? 'bg-line-strong' : 'bg-gold'}`}
                style={{
                  left: centre - d / 2,
                  bottom: bottom + LEAF_H / 2 - d / 2,
                  width: d,
                  height: d,
                  opacity: dead ? 0.7 : 1,
                }}
              />
            );
          }
          /**
           * A leaf angles up and away from the stem, and the droop rotates it down toward the pot.
           * The two sides need opposite signs: a positive rotation drops the far end of a leaf
           * that extends right and lifts the far end of one that extends left. `LEAF_TILT` is the
           * board's own lift — both its leaves run out 26 and up 14, which is 28°.
           */
          const tilt = (right ? -1 : 1) * (LEAF_TILT - wilt * 46);
          return (
            <View
              key={`leaf-${i}`}
              /* The two living tokens alternate with the side the leaf extends to, which is how
                 the board cuts it: leaf-1 on the left is `.leaf` (plant) and leaf-2 on the right
                 is `.leaf-2` (plant-2). Tuxedo declares both as one colour, so there the
                 alternation costs nothing and shows nothing — correct in both rooms. */
              className={dead ? 'bg-line-strong' : right ? 'bg-plant-2' : 'bg-plant'}
              style={{
                position: 'absolute',
                bottom,
                left: right ? centre : undefined,
                right: right ? undefined : centre,
                width: LEAF_W,
                height: LEAF_H,
                // A lens: round on the two opposite corners, pointed on the other two.
                borderTopLeftRadius: right ? LEAF_H : 2,
                borderBottomRightRadius: right ? LEAF_H : 2,
                borderTopRightRadius: right ? 2 : LEAF_H,
                borderBottomLeftRadius: right ? 2 : LEAF_H,
                opacity: dead ? 0.8 : 1 - wilt * 0.5,
                transform: [{ rotate: `${tilt}deg` }],
              }}
            />
          );
        })}

        {/* Won: a bloom on the crown. The only thing on the plant that is not a leaf or a stem —
            and it is gold rather than living tissue, because it is a bud that opened: the reward
            the whole plant was growing toward, in the colour this round pays rewards in. */}
        {won ? (
          <>
            <View
              className="bg-gold absolute rounded-full opacity-20"
              style={{ left: centre - 13, bottom: POT_H + stemH - 13, width: 26, height: 26 }}
            />
            <View
              className="bg-gold absolute rounded-full"
              style={{ left: centre - 7, bottom: POT_H + stemH - 7, width: 14, height: 14 }}
            />
          </>
        ) : null}

        {/* One fallen leaf per miss, lying to the RIGHT of the pot — the board's `.leaf-fallen`,
            which is a whole leaf on the soil rather than a scatter of husks. The countable half of
            the wilt: a droop says "going badly" and these say how many nights it took to get
            there. The first sits where the board puts it; the rest pile down and away from it. */}
        {Array.from({ length: Math.min(coop.misses, MAX_FALLEN) }, (_, k) => (
          <View
            key={`fallen-${k}`}
            className="bg-line-strong absolute"
            style={{
              left: 84 + (k % 2) * 5,
              bottom: 18 - k * 3,
              width: FALLEN_W,
              height: FALLEN_H,
              // The same lens as a living leaf, pointing back at the pot.
              borderTopLeftRadius: 2,
              borderBottomRightRadius: 2,
              borderTopRightRadius: FALLEN_H,
              borderBottomLeftRadius: FALLEN_H,
              opacity: 0.9,
              transform: [{ rotate: `${k % 2 === 0 ? -12 : 6}deg` }],
            }}
          />
        ))}

        {/**
         * The pot: the board's trapezoid, 40 across the rim and 32 at the foot over 12 deep. React
         * Native draws no polygons, so the taper is the transparent-flanks border trick — a top
         * border whose outer edge is the border box (40) and whose inner edge is the content box
         * (32). A plain rectangle of the FOOT's width sits behind it, so that a runtime which
         * ignores a per-side border colour draws a narrower pot rather than no pot at all: an
         * invisible pot is the failure this whole round is fixing, one object over.
         */}
        <View
          className="bg-plant-pot absolute"
          style={{ left: centre - POT_BOT_W / 2, bottom: 0, width: POT_BOT_W, height: POT_H }}
        />
        <View
          className="border-t-plant-pot absolute"
          style={{
            left: centre - POT_TOP_W / 2,
            bottom: 0,
            width: POT_BOT_W,
            height: 0,
            borderTopWidth: POT_H,
            borderLeftWidth: (POT_TOP_W - POT_BOT_W) / 2,
            borderRightWidth: (POT_TOP_W - POT_BOT_W) / 2,
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
          }}
        />
      </View>
    </View>
  );
}
